import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { listKlienci } from '../../api/account'
import { klienciWlasciwi } from '../../lib/pulpit/pulpit'
import { MIESIACE, RODZAJE, isoDnia, krotkaData, siatkaMiesiaca, terminyFirmy, wToku, zIso } from '../../lib/klienci/kalendarz'

/**
 * Przesuwane okno z kalendarzem firmy przy terminie montażu w ofercie (prośba właściciela 2026-11-02): firma widzi swoje
 * spotkania, montaże i serwisy, nie wychodząc z formularza, i może wskazać wolny termin dwoma kliknięciami w dni.
 * Nie zasłania strony tłem, więc formularz zostaje pod ręką. Przeciąganie za nagłówek jak w `PriceHistoryChart`.
 */
const SZEROKOSC = 340
const DNI = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd']
const clamp = (v, min, max) => Math.max(min, Math.min(max, v))

/** Kolejny stan wyboru po kliknięciu dnia: pierwszy klik zaczyna, drugi domyka (w dowolnej kolejności), trzeci zaczyna od nowa. */
export const nastepnyZakres = (wybor, iso) => {
  if (!wybor?.od || wybor.domkniety) return { od: iso, do: iso, domkniety: false }
  return { od: iso < wybor.od ? iso : wybor.od, do: iso < wybor.od ? wybor.od : iso, domkniety: true }
}

const OknoKalendarzaFirmy = ({ od, do: doDnia, onWybierz, onZamknij }) => {
  const dzis = isoDnia(new Date())
  const start = zIso(od || dzis)
  const [miesiac, setMiesiac] = useState({ rok: start.getFullYear(), miesiac: start.getMonth() })
  const [terminy, setTerminy] = useState([])
  const [blad, setBlad] = useState(false)
  // Termin wpisany wcześniej traktujemy jak domknięty, więc pierwszy klik w oknie zaczyna nowy.
  const [wybor, setWybor] = useState(od ? { od, do: doDnia || od, domkniety: true } : null)

  useEffect(() => {
    let anulowane = false
    listKlienci().then((r) => {
      if (anulowane) return
      if (!r.ok) { setBlad(true); return }
      setTerminy(terminyFirmy(klienciWlasciwi(r.data.klienci)).filter(wToku))
    })
    return () => { anulowane = true }
  }, [])

  const tygodnie = useMemo(() => siatkaMiesiaca(miesiac.rok, miesiac.miesiac, terminy), [miesiac, terminy])
  const przesun = (o) => setMiesiac((m) => {
    const d = new Date(m.rok, m.miesiac + o, 1)
    return { rok: d.getFullYear(), miesiac: d.getMonth() }
  })
  const kliknij = (iso) => {
    const nowy = nastepnyZakres(wybor, iso)
    setWybor(nowy)
    onWybierz(nowy.od, nowy.do)
  }

  const okno = useRef(null)
  const chwyt = useRef(null)
  const [poz, setPoz] = useState(null)
  const wGranicach = useCallback((x, y) => {
    const w = okno.current?.offsetWidth || SZEROKOSC
    const h = okno.current?.offsetHeight || 420
    return { x: clamp(x, 8, Math.max(8, window.innerWidth - w - 8)), y: clamp(y, 8, Math.max(8, window.innerHeight - h - 8)) }
  }, [])
  useEffect(() => {
    setPoz(wGranicach(window.innerWidth - SZEROKOSC - 40, 96))
    const naZmiane = () => setPoz((p) => (p ? wGranicach(p.x, p.y) : p))
    const naKlawisz = (e) => { if (e.key === 'Escape') onZamknij() }
    window.addEventListener('resize', naZmiane)
    window.addEventListener('keydown', naKlawisz)
    return () => {
      window.removeEventListener('resize', naZmiane)
      window.removeEventListener('keydown', naKlawisz)
    }
  }, [wGranicach, onZamknij])
  const zacznijPrzeciaganie = (e) => {
    if (e.target.closest('button') || !okno.current) return
    const r = okno.current.getBoundingClientRect()
    chwyt.current = { dx: e.clientX - r.left, dy: e.clientY - r.top }
    const ruch = (ev) => { if (chwyt.current) setPoz(wGranicach(ev.clientX - chwyt.current.dx, ev.clientY - chwyt.current.dy)) }
    const koniec = () => {
      chwyt.current = null
      window.removeEventListener('pointermove', ruch)
      window.removeEventListener('pointerup', koniec)
    }
    window.addEventListener('pointermove', ruch)
    window.addEventListener('pointerup', koniec)
  }

  return (
    <div ref={okno} role="dialog" aria-label="Kalendarz firmy" style={poz ? { left: poz.x, top: poz.y } : { visibility: 'hidden' }}
         className="fixed z-50 max-h-[calc(100dvh-16px)] w-[340px] max-w-[calc(100vw-16px)] overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-2xl">
      <div onPointerDown={zacznijPrzeciaganie} style={{ touchAction: 'none' }}
           className="flex cursor-move select-none items-center justify-between gap-2 border-b border-gray-100 px-4 py-2.5">
        <h2 className="m-0 text-sm font-bold text-gray-900">Twój kalendarz</h2>
        <button type="button" onClick={onZamknij} aria-label="Zamknij kalendarz"
                className="-mr-2 inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100">
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => przesun(-1)} aria-label="Poprzedni miesiąc" className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100">
            <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
          </button>
          <span className="text-sm font-semibold text-gray-900">{MIESIACE[miesiac.miesiac]} {miesiac.rok}</span>
          <button type="button" onClick={() => przesun(1)} aria-label="Następny miesiąc" className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100">
            <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="mt-1 grid grid-cols-7 text-center text-[11px] font-medium text-gray-500" aria-hidden="true">
          {DNI.map((d) => <span key={d}>{d}</span>)}
        </div>
        <div className="mt-1 grid grid-cols-7 gap-y-0.5">
          {tygodnie.flat().map((d) => {
            const wZakresie = wybor && d.iso >= wybor.od && d.iso <= wybor.do
            const minal = d.iso < dzis
            const opis = d.odcinki.map((x) => `${RODZAJE[x.termin.rodzaj]?.etykieta || 'Termin'}: ${x.termin.klient?.nazwa || ''}`.trim())
            return (
              <button key={d.iso} type="button" disabled={minal} onClick={() => kliknij(d.iso)} aria-pressed={Boolean(wZakresie)}
                      aria-label={[`${d.iso}`, d.swieto, ...opis].filter(Boolean).join(', ')} title={[d.swieto, ...opis].filter(Boolean).join('\n') || undefined}
                      className={`relative flex h-10 flex-col items-center justify-center rounded-lg text-[13px] tabular-nums transition
                        ${wZakresie ? 'bg-blue-600 font-semibold text-white' : minal ? 'text-gray-300' : 'hover:bg-gray-100'}
                        ${!wZakresie && !minal ? (d.wMiesiacu ? (d.weekend || d.swieto ? 'text-gray-400' : 'text-gray-900') : 'text-gray-300') : ''}
                        ${d.iso === dzis && !wZakresie ? 'font-bold text-blue-700' : ''}`}>
                {zIso(d.iso).getDate()}
                {d.odcinki.length > 0 && (
                  <span className="mt-0.5 flex gap-0.5" aria-hidden="true">
                    {d.odcinki.slice(0, 3).map((x) => (
                      <span key={x.termin.id} className="h-1.5 w-1.5 rounded-full" style={{ background: wZakresie ? '#FFFFFF' : RODZAJE[x.termin.rodzaj]?.kropka || '#94A3B8' }} />
                    ))}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <p className="m-0 mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-gray-600">
          {Object.values(RODZAJE).map((r) => (
            <span key={r.etykieta} className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: r.kropka }} aria-hidden="true" />{r.etykieta}
            </span>
          ))}
        </p>
        {blad && <p role="alert" className="m-0 mt-2 text-[13px] text-gray-700">Nie udało się wczytać Twoich terminów. Wolny termin wybierzesz mimo to.</p>}
        <p className="m-0 mt-2 text-[13px] leading-5 text-gray-700">
          {wybor?.od
            ? <>Wolny termin w ofercie: <strong>{krotkaData(wybor.od)}{wybor.do !== wybor.od ? ` do ${krotkaData(wybor.do)}` : ''}</strong></>
            : 'Kliknij pierwszy i ostatni wolny dzień, a termin wpisze się do oferty.'}
        </p>
        <Link to="/klienci?widok=kalendarz" target="_blank" rel="noopener" className="mt-1 inline-block text-[13px] font-semibold text-blue-700 hover:underline">
          Otwórz pełny kalendarz
        </Link>
      </div>
    </div>
  )
}

export default OknoKalendarzaFirmy
